import { validatePlayerId } from "@/embed/context"

/**
 * Recent-player quick-switch persistence for the support workspace.
 *
 * The list holds ONLY player ids the agent typed, already validated by the
 * same `validatePlayerId` gate that host-supplied ids pass through, and is
 * re-validated on the way back out of storage: localStorage is shared,
 * hand-editable state, so it gets the same treatment as any other untrusted
 * source. No other player attribute (name, email, anything) is ever stored.
 */

/** localStorage key holding the agent's recent player ids, most recent first. */
export const RECENT_PLAYERS_KEY = "support:recentPlayers"

/** Maximum entries kept: enough for a shift's active tickets, small enough to scan. */
export const RECENT_PLAYERS_MAX = 8

/**
 * Load the recent-player list from storage.
 *
 * Corrupt or hand-edited values degrade to an empty (or filtered) list rather
 * than throwing: losing quick-switch chips is the correct failure mode for a
 * convenience cache.
 *
 * @param storage The Storage to read from (window.localStorage in prod).
 * @returns Validated player ids, most recent first, capped at RECENT_PLAYERS_MAX.
 */
export function loadRecentPlayers(storage: Storage): string[] {
  const raw = storage.getItem(RECENT_PLAYERS_KEY)
  if (raw === null) return []
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []
  return parsed
    .map(entry => (typeof entry === "string" ? validatePlayerId(entry) : null))
    .filter((id): id is string => id !== null)
    .slice(0, RECENT_PLAYERS_MAX)
}

/**
 * Record a player id as most recently used and persist the updated list.
 *
 * @param playerId The player id the agent just loaded. Re-validated here; an
 *   invalid value changes nothing (callers validate first, this is the
 *   belt-and-braces gate before anything touches storage).
 * @param storage The Storage to persist into (window.localStorage in prod).
 * @returns The updated list, most recent first, deduplicated and capped.
 */
export function pushRecentPlayer(playerId: string, storage: Storage): string[] {
  const validated = validatePlayerId(playerId)
  const existing = loadRecentPlayers(storage)
  if (validated === null) return existing
  const next = [validated, ...existing.filter(id => id !== validated)].slice(0, RECENT_PLAYERS_MAX)
  storage.setItem(RECENT_PLAYERS_KEY, JSON.stringify(next))
  return next
}
